import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import { Box, Text, Heading } from '@primer/react';
import Reveal from '../components/Reveal';
import ServiceCTA from '../components/ServiceCTA';
import SEO from '../components/SEO';
import { getBlogPost } from '../data/blogPosts';
import { breadcrumbSchema, articleSchema, SITE_URL } from '../utils/seo';

function BlogPost() {
  const { slug } = useParams();
  const post = getBlogPost(slug);

  if (!post) {
    return <Navigate to="/insights" replace />;
  }

  const schema = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Insights', path: '/insights' },
      { name: post.title, path: `/insights/${post.slug}` },
    ]),
    articleSchema({
      title: post.title,
      description: post.seoDescription,
      path: `/insights/${post.slug}`,
      datePublished: post.date,
    }),
  ];

  return (
    <Box sx={{ backgroundColor: 'canvas.default' }}>
      <SEO
        title={post.title}
        description={post.seoDescription}
        keywords={post.seoKeywords}
        path={`/insights/${post.slug}`}
        schema={schema}
        image={`${SITE_URL}/og/blog-${post.slug}.jpg`}
      />

      <Box
        sx={{
          paddingX: ['24px', '48px', '100px'],
          paddingTop: [6, 7, '4vh'],
          paddingBottom: [6, 7, 8],
        }}
      >
        <Box sx={{ maxWidth: '760px', margin: '0 auto' }}>
          <Reveal>
            <Box
              as={RouterLink}
              to="/insights"
              sx={{
                display: 'inline-block',
                marginBottom: 5,
                fontSize: 0,
                fontWeight: 600,
                color: 'fg.muted',
                textDecoration: 'none',
                ':hover': { color: 'fg.default' },
              }}
            >
              &larr; Back to Insights
            </Box>

            <Text
              as="p"
              sx={{
                margin: 0,
                marginBottom: 2,
                fontSize: 0,
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'accent.fg',
              }}
            >
              {post.category}
            </Text>

            <Heading
              as="h1"
              sx={{
                margin: 0,
                marginBottom: 4,
                fontFamily: "'Fraunces', serif",
                fontWeight: 300,
                fontSize: ['2rem', '2.75rem', '3.25rem'],
                lineHeight: 1.2,
                color: 'fg.default',
              }}
            >
              {post.title}
            </Heading>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 6 }}>
              <Text sx={{ fontSize: 1, color: 'fg.muted' }}>
                {new Date(`${post.date}T00:00:00`).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Text>
              <Text sx={{ fontSize: 1, color: 'fg.muted' }}>{post.readTime}</Text>
            </Box>
          </Reveal>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {post.content.map((block, index) => {
              if (block.type === 'heading') {
                return (
                  <Reveal key={index}>
                    <Heading
                      as="h2"
                      sx={{
                        margin: 0,
                        marginTop: 3,
                        fontFamily: "'Fraunces', serif",
                        fontWeight: 500,
                        fontSize: [3, 4],
                        color: 'fg.default',
                      }}
                    >
                      {block.text}
                    </Heading>
                  </Reveal>
                );
              }

              if (block.type === 'list') {
                return (
                  <Reveal key={index}>
                    <Box as="ul" sx={{ margin: 0, paddingLeft: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {block.items.map((item) => (
                        <Text as="li" key={item} sx={{ fontSize: 2, lineHeight: 1.8, color: 'fg.muted' }}>
                          {item}
                        </Text>
                      ))}
                    </Box>
                  </Reveal>
                );
              }

              return (
                <Reveal key={index}>
                  <Text as="p" sx={{ margin: 0, fontSize: 2, lineHeight: 1.8, color: 'fg.muted' }}>
                    {block.text}
                  </Text>
                </Reveal>
              );
            })}
          </Box>
        </Box>
      </Box>

      <ServiceCTA
        eyebrow="Stop guessing. Start knowing."
        headline={[
          { text: 'Want a second set of', tone: 'plain' },
          { text: 'eyes on your product?', tone: 'gradient' },
        ]}
        subtitle="Book a UX audit and get a prioritized report using the exact framework in this article."
      />
    </Box>
  );
}

export default BlogPost;
